import React from 'react'
import { useParams, Link } from 'react-router-dom'

const InvoiceTypesPage = () => {
  const { type } = useParams()

  const invoiceTypes = {
    proforma: {
      title: 'Proforma Invoice',
      subtitle: 'What is a Proforma Invoice?',
      icon: '📋',
      description: 'A proforma invoice is a preliminary bill of sale sent to buyers in advance of a shipment or delivery of goods. It outlines the commitment to provide goods or services at a specified price.',
      keyPoints: [
        'Not a true invoice - acts as a quote or estimate',
        'Used for customs declarations in international trade',
        'Shows what the buyer can expect to receive',
        'No payment obligation until final invoice is issued',
        'Essential for securing import permits and letters of credit'
      ],
      useCases: [
        'International shipping and exports',
        'Customs clearance documentation',
        'Buyer pre-approval before production',
        'Securing financing or letters of credit'
      ],
      color: 'from-blue-600 to-indigo-600'
    },
    tax: {
      title: 'Tax Invoice',
      subtitle: 'What is a Tax Invoice?',
      icon: '🧾',
      description: 'A tax invoice is a legal document that shows the amount of tax (VAT, GST, sales tax) charged on goods or services. It\'s required by tax authorities and allows buyers to claim tax credits.',
      keyPoints: [
        'Must include your tax registration number (VAT/GST ID)',
        'Shows itemized tax amounts for each line item',
        'Required by law in most jurisdictions',
        'Enables buyers to claim input tax credits',
        'Must be retained for tax audit purposes'
      ],
      useCases: [
        'VAT-registered businesses in Europe',
        'GST-compliant invoicing in Australia, Canada, India',
        'Sales tax documentation in the United States',
        'Any business required to collect taxes'
      ],
      color: 'from-emerald-600 to-teal-600'
    },
    medical: {
      title: 'Medical Invoice',
      subtitle: 'Healthcare Billing Made Simple',
      icon: '🏥',
      description: 'A medical invoice documents healthcare services provided to patients. It includes procedure codes, insurance information, and itemized charges for medical treatments, consultations, or supplies.',
      keyPoints: [
        'Includes CPT/ICD codes for procedures and diagnoses',
        'Patient and insurance company details',
        'Itemized list of services and medications',
        'Insurance claim reference numbers',
        'HIPAA-compliant documentation'
      ],
      useCases: [
        'Private practice clinics and doctors',
        'Physical therapy and rehabilitation centers',
        'Dental and orthodontic practices',
        'Home healthcare providers',
        'Medical equipment suppliers'
      ],
      color: 'from-red-600 to-pink-600'
    },
    legal: {
      title: 'Legal Invoice',
      subtitle: 'Attorney and Law Firm Billing',
      icon: '⚖️',
      description: 'A legal invoice itemizes legal services provided by attorneys, law firms, or legal consultants. It typically includes hourly rates, billable hours, case references, and retainer information.',
      keyPoints: [
        'Detailed time tracking by attorney and task',
        'Hourly billing rates or flat fee arrangements',
        'Case or matter numbers for reference',
        'Retainer balance tracking',
        'Expense reimbursements (court fees, filing costs)'
      ],
      useCases: [
        'Law firms and attorneys',
        'Legal consultants and advisors',
        'Corporate legal departments',
        'Contract lawyers and freelance attorneys',
        'Paralegal and legal support services'
      ],
      color: 'from-slate-700 to-gray-800'
    }
  }

  const currentType = type ? invoiceTypes[type as keyof typeof invoiceTypes] : invoiceTypes.proforma

  if (!currentType) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Invoice Type Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline">
            Return to Homepage
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${currentType.color} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-6xl">{currentType.icon}</span>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                {currentType.title}
              </h1>
              <p className="text-xl opacity-90">
                {currentType.subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Description */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <p className="text-lg text-gray-700 leading-relaxed">
            {currentType.description}
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Key Points */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-emerald-600 mr-3">✓</span>
              Key Features
            </h2>
            <ul className="space-y-4">
              {currentType.keyPoints.map((point, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-blue-600 font-bold mr-3 mt-1">•</span>
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Use Cases */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-amber-600 mr-3">📌</span>
              Common Use Cases
            </h2>
            <ul className="space-y-4">
              {currentType.useCases.map((useCase, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-emerald-600 font-bold mr-3 mt-1">→</span>
                  <span className="text-gray-700">{useCase}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`bg-gradient-to-r ${currentType.color} rounded-3xl shadow-2xl p-12 text-center text-white`}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Create Your {currentType.title} Now
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Use our free invoice generator to create professional {currentType.title.toLowerCase()}s in seconds. No sign-up required.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-gray-900 hover:bg-gray-100 font-bold text-lg py-4 px-10 rounded-full transition-all duration-200 hover:scale-105 shadow-lg"
          >
            Start Creating Invoices →
          </Link>
        </div>

        {/* Navigation to Other Types */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Explore Other Invoice Types
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(invoiceTypes)
              .filter(([key]) => key !== type)
              .map(([key, data]) => (
                <Link
                  key={key}
                  to={`/invoice-types/${key}`}
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className="text-4xl mb-3">{data.icon}</div>
                  <h4 className="font-bold text-gray-900 mb-2">{data.title}</h4>
                  <p className="text-sm text-gray-600">Learn more →</p>
                </Link>
              ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Do I need special software to create a {currentType.title.toLowerCase()}?
              </h4>
              <p className="text-gray-600">
                No! Our online invoice generator creates {currentType.title.toLowerCase()}s instantly in your browser. No downloads or installations required.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Is the {currentType.title.toLowerCase()} legally valid?
              </h4>
              <p className="text-gray-600">
                Yes. Our invoices include all required legal elements and comply with international invoicing standards. Always consult local regulations for specific requirements.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Can I customize the invoice template?
              </h4>
              <p className="text-gray-600">
                Absolutely! Add your logo, adjust colors, include custom fields, and personalize every aspect of your {currentType.title.toLowerCase()}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceTypesPage
